import React from "react";
import history from "Services/history";
import { geo_aggregations_lookup } from "Lookups";
import { geoAggregations } from "Data";
import withState from "recompose/withState";
import lifecycle from "recompose/lifecycle";
import compose from "recompose/compose";
import databaseCache from "Services/databaseCache";
import simplifyName from "Utils/simplifyName";
import shouldUpdate from "recompose/shouldUpdate";
import { getChartsForDomainData } from "Components/Charts/componentFilter";
import config from "Config";
import capitalizeSentence from "Utils/capitalizeSentence";
import round from "Utils/round";

const summaryPipeline = () => props =>
  props.chain(
    ([
      value_wa, // "COMMUNITY_QUERY"
      value_div, // "COMMUNITY_QUERY_DIVISION",
      total_for_geo_aggregation, // "TOTAL_POPULATION",
      total_population_of_wa // "TOTAL_POPULATION_STATE
    ]) => ({
      value_wa: value_wa.rows[0].number,
      value_div: value_div ? value_div.rows[0].number : null,
      total_for_geo_aggregation: total_for_geo_aggregation
        ? total_for_geo_aggregation.rows[0].number
        : null,
      total_population_of_wa: total_population_of_wa
        ? total_population_of_wa.rows[0].number
        : null
    })
  );

const addViewState = withState("viewState", "setViewState", {
  value_wa: null,
  value_div: null,
  total_for_geo_aggregation: null,
  total_population_of_wa: null
});

const Lifecycle = lifecycle({
  propsChanged(domain_data) {
    return [
      "year",
      "community",
      "community_value",
      "geo_aggregation",
      "geo_aggregation_value"
    ].reduce(
      (memo, key) => memo || domain_data[key] != this.props.domain_data[key],
      false // default: props have not changed
    );
  },
  componentDidMount() {
    this.ignore_incomming = false;
    if (this.props.domain_data.community_value) {
      this.refreshData(this.props.domain_data);
    }
  },
  componentWillUnmount() {
    this.ignore_incomming = true;
  },
  componentWillReceiveProps({ domain_data }) {
    if (domain_data.community_value && this.propsChanged(domain_data)) {
      this.refreshData(domain_data);
    }
  },
  refreshData(domain_data) {
    let queries = domain_data.geo_aggregation_value
      ? [
          "COMMUNITY_QUERY",
          "COMMUNITY_QUERY_DIVISION",
          "TOTAL_POPULATION",
          "TOTAL_POPULATION_STATE"
        ]
      : ["COMMUNITY_QUERY"];

    databaseCache
      .getData(queries, undefined, domain_data, summaryPipeline())
      .then(this.props.setViewState.bind(this));

    this.props.setViewState({});
  }
});

const element = ({ icon, text }) => [
  <i key={icon} className={`fa fa-${icon}`} />,
  <span key={text} dangerouslySetInnerHTML={{ __html: text }} />
];

const getPercentage = (value, total_for_geo_aggregation) =>
  round(1)((value / total_for_geo_aggregation) * 100);

const titleTextPrepare = ({
  value = "",
  community,
  community_value,
  geo_aggregation_value,
  total_for_geo_aggregation
}) =>
  getPercentage(value, total_for_geo_aggregation).chain(percent =>
    element(
      {
        Birthplace: () => ({
          icon: "birthday-cake",
          text: ` <span class="bg-highlight">${value.toLocaleString()}</span> individuals born in <span class="bg-highlight">${community_value}</span> reside in <span class="bg-highlight">${geo_aggregation_value ||
            config.admin_state_name}</span> out of the total population of <span class="bg-highlight">${total_for_geo_aggregation.toLocaleString()}</span>. This is <span class="bg-highlight">${percent}%</span> of the total population of <span class="bg-highlight">${geo_aggregation_value ||
            config.admin_state_name}</span>.`
        }),
        Ancestry: () => ({
          icon: "pagelines",
          text: ` <span class="bg-highlight">${value.toLocaleString()}</span> individuals of <span class="bg-highlight">${community_value}</span> ancestry reside in ${geo_aggregation_value ||
            config.admin_state_name} out of the total population of <span class="bg-highlight">${total_for_geo_aggregation.toLocaleString()}</span>. This is <span class="bg-highlight">${percent}%</span> of the total population of <span class="bg-highlight">${geo_aggregation_value ||
            config.admin_state_name}</span>.`
        }),
        Language: () => ({
          icon: "language",
          text: ` <span class="bg-highlight">${value.toLocaleString()}</span> individuals speak <span class="bg-highlight">${community_value}</span> at home in ${geo_aggregation_value ||
            config.admin_state_name} out of the total population of <span class="bg-highlight">${total_for_geo_aggregation.toLocaleString()}</span>. This is <span class="bg-highlight">${percent}%</span> of the total population of <span class="bg-highlight">${geo_aggregation_value ||
            config.admin_state_name}</span>.`
        }),
        Religion: () => ({
          icon: "book",
          text: ` <span class="bg-highlight">${value.toLocaleString()}</span> individuals identify with <span class="bg-highlight">${community_value}</span> in <span class="bg-highlight">${geo_aggregation_value ||
            config.admin_state_name}</span> out of the total population of <span class="bg-highlight">${total_for_geo_aggregation.toLocaleString()}</span>. This is <span class="bg-highlight">${percent}%</span> of the total population of <span class="bg-highlight">${geo_aggregation_value ||
            config.admin_state_name}</span>.`
        })
      }[community]()
    )
  );

// this view is for when nothing except a geo_aggregation is selected
// it breaks down the detailed stats by selected geo_aggregation
// and has the 5 different categories stated in issue @geografia-au/dlgsc#10
// TODO: Code Smell: this is pretty adhoc needs to fit into a formal spec
// Also see: @geografia-au/dlgrma#84
// const rel1Names = [
//   "Not stated",
//   "Hinduism",
//   "Christianity",
//   "Buddhism",
//   "Secular Beliefs and Other Spiritual Beliefs and No Religious Affiliation",
//   "Other Religions",
//   "Judaism",
//   "Inadequately described",
//   "Islam"
// ];

const DivisionView = ({ domain_data, viewState }) =>
  simplifyName(domain_data.geo_aggregation_value).chain(nice_name => (
    <div>
      <b className="text-capitalize">
        {geo_aggregations_lookup[domain_data.geo_aggregation]}
      </b>
      <h3 className="pt-2 pb-2">
        <b>
          {nice_name.replace(/_/g, " ")}
          <small className="pl-1 smallcaps">{domain_data.community}</small>
        </b>
      </h3>
      <div className="text-justify">
        <p>
          <b>
            {titleTextPrepare({
              ...domain_data,
              value: viewState.value_div !== null ? viewState.value_div : "",
              geo_aggregation_value: nice_name.replace(/_/g, " "),
              total_for_geo_aggregation:
                viewState.total_for_geo_aggregation || ""
            })}
          </b>
        </p>
        <p>
          {titleTextPrepare({
            ...domain_data,
            value: viewState.value_wa !== null ? viewState.value_wa : "",
            geo_aggregation_value: config.admin_state_name,
            total_for_geo_aggregation: viewState.total_population_of_wa || ""
          })}
        </p>
      </div>
      <button
        type="button"
        className="mt-2 btn btn-info btn-block"
        onClick={() =>
          history.push(
            `/${domain_data.geo_aggregation}/${nice_name}/${domain_data.year}`
          )
        }
      >
        <b>
          See full details for
          <br /> {nice_name.replace(/_/g, " ")}
        </b>
        <i className="fa pl-2 fa-arrow-right" />
      </button>
      <button
        type="button"
        className="mt-2 btn btn-info btn-block"
        onClick={() =>
          history.push(
            `/${domain_data.community}/${domain_data.community_value}/${domain_data.year}`
          )
        }
      >
        <b>
          See full details for
          <br /> {domain_data.community_value} ({domain_data.community})
        </b>
        <i className="fa pl-2 fa-arrow-right" />
      </button>
    </div>
  ));

const KeyStatisticsView = ({ domain_data }) =>
  // do we show for geo_aggregation or
  // community ?
  (domain_data.community_value ? "community" : "geo_aggregation").chain(
    showFor =>
      ({
        stats_value:
          showFor == "geo_aggregation"
            ? domain_data.geo_aggregation_value
              ? `${
                  domain_data.geo_aggregation_value
                } (${domain_data.geo_aggregation.toUpperCase()})`
              : `all of ${config.admin_state_name}`
            : (domain_data.community_value
                ? `${domain_data.community_value} (${domain_data.community}) in `
                : "") + `all of ${config.admin_state_name}`,
        filteredChartComponents: getChartsForDomainData(domain_data, showFor)[
          "Key Statistics"
        ],
        details_link:
          showFor == "community"
            ? "/" +
              domain_data.community +
              "/" +
              domain_data.community_value +
              "/" +
              domain_data.year
            : "/" +
              (domain_data.geo_aggregation_value !== ""
                ? domain_data.geo_aggregation
                : "ste") +
              "/" +
              simplifyName(
                domain_data.geo_aggregation_value ||
                  geoAggregations[domain_data.year]["ste"][0]
              ) +
              "/" +
              domain_data.year
      }.chain(({ stats_value, filteredChartComponents, details_link }) => (
        <div>
          <h3>Key statistics for {stats_value}</h3>
          <button
            type="button"
            className="mt-2 btn btn-block btn-info"
            onClick={() => history.push(details_link)}
          >
            <b>Go to full page</b>
            <i className="fa pl-2 fa-arrow-right" />
          </button>
          <div className="mb-4"></div>{" "}
          {/* Render out all the Introductory Charts */}
          {Object.keys(filteredChartComponents).map(key =>
            ({
              title: key,
              Chart: filteredChartComponents[key]["component"]
            }.chain(({ title, Chart }) => (
              <React.Fragment key={title}>
                <div
                  style={
                    key === "Population Composition"
                      ? { fontSize: "1.2rem", marginBottom: "10px" }
                      : {}
                  }
                >
                  {key === "Population Composition" ? "" : <h5>{title}</h5>}
                  <Chart
                    showFor={showFor}
                    domain_data={domain_data}
                    hide_table={key === "Population Composition"}
                    hideFootnote={key === "Population Composition"}
                    isInfoPane={true}
                  />
                </div>
                {key === "Population Composition" ? <br /> : <hr />}
              </React.Fragment>
            )))
          )}
          <button
            type="button"
            className="mt-2 btn btn-block btn-info"
            onClick={() => history.push(details_link)}
          >
            <b>Go to full page</b>
            <i className="fa pl-2 fa-arrow-right" />
          </button>
        </div>
      )))
  );

const Template = ({ domain_data, viewState }) => (
  <div id="info-pane" className="p-4 white">
    {domain_data.geo_aggregation_value &&
    domain_data.geo_aggregation !== "ste" &&
    domain_data.community_value ? (
      <DivisionView {...{ domain_data, viewState }} />
    ) : (
      <KeyStatisticsView {...{ domain_data, viewState }} />
    )}
  </div>
);

const guardUpdates = shouldUpdate((props, nextProps) => {
  let answer = false;
  let changed = [
    "year",
    "community",
    "community_value",
    "geo_aggregation",
    "geo_aggregation_value"
  ].reduce(
    (memo, key) =>
      nextProps.domain_data[key] != props.domain_data[key]
        ? memo.concat(key)
        : memo,
    []
  );
  if (
    changed.length > 0 &&
    !(
      changed.length == 1 &&
      changed[0] == "community" &&
      !nextProps.domain_data.community_value
    )
  ) {
    answer = true;
  }
  return answer;
});

export default compose(guardUpdates, addViewState, Lifecycle)(Template);



// WEBPACK FOOTER //
// ./components/InfoPane.js